/**
 * Histogramme. Quand `display.drill_dimensions` est renseigné, cliquer une barre
 * descend d'un niveau (cf. `useDrillDown`) : les barres affichées deviennent celles
 * du niveau descendu, sur une série unique (la comparaison n'a plus de sens hors du
 * niveau 0). Sans `drill_dimensions`, le widget reste inerte au clic.
 */
import ReactECharts from 'echarts-for-react';
import type { WidgetConfig } from '../schemas';
import { formatDateLabel, resolveDimensionKey, isTimeDimensionKey } from '../chartFormat';
import { useFieldCatalog, metricLabel, metricFormat } from '../fieldCatalog';
import type { MetricFormat } from '../formatValue';
import { useDrillDown } from '../useDrillDown';
import { DrillBreadcrumb } from '../DrillBreadcrumb';
import { useChartPalette } from '../ChartPaletteContext';
import { resolveWidgetPalette } from '../chartPalette';
import {
  COLOR_PREVIOUS,
  AXIS_X_STYLE,
  TITLE_STYLE,
  buildChartGrid,
  buildLegendStyle,
  buildTooltip,
  buildXAxisLabel,
  resolveDualAxis,
  type LegendPosition,
} from '../chartStyle';

type Row = Record<string, string | number>;

interface WidgetProps {
  config: WidgetConfig;
  data: { current: Row[]; previous: Row[] | null; meta: any } | null;
  loading?: boolean;
  error?: string;
}

// Cadre + padding fournis par WidgetShell (WidgetRenderer) : ici, hauteur + flex.
const SHELL = 'h-full flex flex-col';
const DEFAULT_LIMIT = 10;

export default function BarChartWidget({ config, data, loading, error }: WidgetProps) {
  // Hooks avant tout early return (règle des hooks).
  const catalog = useFieldCatalog(data?.meta?.source_type as string | undefined);
  const palette = resolveWidgetPalette(config.format?.palette as string | string[] | undefined, useChartPalette());

  const display = (config.display ?? {}) as Record<string, unknown>;
  const drillDimensions = (display.drill_dimensions as string[] | undefined) ?? [];
  const drillMetric = config.metrics?.[0];
  const baseDimension = config.dimensions?.[0];
  const period = (data?.meta as { period?: { start: string; end: string } } | undefined)?.period;

  const drill = useDrillDown({
    enabled:
      drillDimensions.length > 0 &&
      !!config.data_source_id &&
      !!drillMetric &&
      !!baseDimension &&
      !!period,
    dimension: baseDimension ?? '',
    drillDimensions,
    dataSourceId: config.data_source_id ?? '',
    metric: drillMetric ?? '',
    topN: (display.limit as number | undefined) ?? DEFAULT_LIMIT,
    period: period ?? { start: '', end: '' },
  });

  // Un échec de drill-down (400, timeout…) s'affiche dans l'état d'erreur du widget :
  // sans cela, le clic resterait sans réponse visible.
  const shownError = error ?? drill.error;

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (shownError) {
    return (
      <div className={`${SHELL} flex items-center justify-center`}>
        <span className="text-sm text-red-600">{shownError}</span>
      </div>
    );
  }

  const allMetrics = config.metrics ?? [];

  if (!data || !config.data_source_id || !allMetrics.length) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  // Niveau descendu : la dimension interrogée est celle du dernier cran de la pile,
  // et le résultat est une série unique (une métrique, pas de période précédente).
  const isDrilled = drill.drillPath.length > 0 && !!drill.drillItems;
  const drilledDimension = drillDimensions[drill.drillPath.length - 1];

  const metrics = isDrilled ? allMetrics.slice(0, 1) : allMetrics;
  const dimensions = isDrilled ? [drilledDimension] : config.dimensions;
  const allCurrentRows: Row[] = isDrilled
    ? (drill.drillItems ?? []).map((item) => ({
        [drilledDimension]: item.key,
        [metrics[0]]: item.value,
      }))
    : data.current;
  const allPreviousRows: Row[] | null = isDrilled ? null : data.previous;

  // Axe X catégoriel : on conserve l'ordre renvoyé (souvent trié par métrique) et on
  // formate seulement les libellés (no-op sur une valeur non temporelle comme un pays).
  const dimKey = resolveDimensionKey(allCurrentRows, dimensions, metrics);

  // Top-N (`display.limit`) : n'a de sens que sur un axe CATÉGORIEL. Sur un axe temps
  // (issu de la granularité), tronquer amputerait la série — 30 jours tomberaient à 10
  // points, ce qui serait un mensonge graphique. Au niveau descendu, le top-N est déjà
  // appliqué côté serveur (`topN` passé à useDrillDown) : on ne retronque pas.
  const isTimeAxis =
    isTimeDimensionKey(dimKey) ||
    (!!dimKey && !!catalog?.dimensions.find((d) => d.key === dimKey)?.time);
  const limit = display.limit as number | undefined;
  const rowLimit =
    !isDrilled && !isTimeAxis && typeof limit === 'number' && limit > 0 ? limit : undefined;

  // On tronque aussi la période précédente : les séries sont alignées par index sur
  // l'axe catégoriel, une série `previous` plus longue déborderait des catégories.
  const currentRows = rowLimit ? allCurrentRows.slice(0, rowLimit) : allCurrentRows;
  const previousRows: Row[] | null =
    rowLimit && allPreviousRows ? allPreviousRows.slice(0, rowLimit) : allPreviousRows;
  const rawValues = dimKey ? currentRows.map((row) => String(row[dimKey] ?? '')) : [];
  const categories = dimKey ? rawValues.map((v) => formatDateLabel(v, dimKey)) : currentRows.map((_, i) => String(i));

  const legendPosition = (display.legend_position as LegendPosition | undefined) ?? 'bottom';
  const showGrid = (display.show_grid as boolean | undefined) ?? true;
  const showYAxis = (display.show_y_axis as boolean | undefined) ?? true;
  // Orientation : barres horizontales = on intervertit les axes (catégorie sur yAxis,
  // valeur sur xAxis). Par défaut vertical (comportement historique). L'axe des valeurs
  // reste piloté par `resolveDualAxis`/`show_y_axis` ; il devient juste l'axe X.
  const horizontal = (display.horizontal as boolean | undefined) ?? false;
  // Coins arrondis côté croissance de la barre : haut en vertical, droite en horizontal.
  const barRadius = horizontal ? [0, 3, 3, 0] : [3, 3, 0, 0];
  // Clé d'index d'axe des valeurs selon l'orientation (l'axe des valeurs bascule sur X).
  const valueAxisKey = horizontal ? 'xAxisIndex' : 'yAxisIndex';

  const seriesColors = (config.format?.series_colors as Record<string, string> | undefined) ?? {};
  const singleColor = config.format?.color as string | undefined;

  const yAxisScale = (display.y_axis_scale as 'linear' | 'log' | undefined) ?? 'linear';

  // Axe Y par série (1 = axe droit) : comparer deux échelles sans écraser la plus petite.
  const { yAxis, axisIndexFor, dual } = resolveDualAxis(
    metrics,
    display.series_y_axis as Record<string, 0 | 1> | undefined,
    (m) => metricFormat(catalog, m),
    showGrid,
    showYAxis,
    yAxisScale,
  );

  const formats: Record<string, MetricFormat> = {};
  const series: object[] = [];

  metrics.forEach((metric, i) => {
    const color =
      seriesColors[metric] ??
      (metrics.length === 1 && singleColor ? singleColor : palette[i % palette.length]);
    const label = metricLabel(catalog, metric, config.metric_labels);
    const format = metricFormat(catalog, metric);
    formats[label] = format;
    const yAxisIndex = axisIndexFor(metric);

    series.push({
      name: label,
      type: 'bar',
      [valueAxisKey]: yAxisIndex,
      data: currentRows.map((row) => Number(row[metric]) || 0),
      itemStyle: { color, borderRadius: barRadius },
      barMaxWidth: metrics.length > 1 ? 24 : 40,
      cursor: drill.canDrillDown ? 'pointer' : 'default',
    });

    if (previousRows && previousRows.length) {
      const prevLabel = `${label} (préc.)`;
      const prevColor = metrics.length > 1 ? color : COLOR_PREVIOUS;
      formats[prevLabel] = format;
      series.push({
        name: prevLabel,
        type: 'bar',
        [valueAxisKey]: yAxisIndex,
        data: previousRows.map((row) => Number(row[metric]) || 0),
        itemStyle: { color: prevColor, opacity: 0.55, borderRadius: barRadius },
        barMaxWidth: metrics.length > 1 ? 24 : 40,
      });
    }
  });

  const showLegend = (display.show_legend as boolean | undefined) ?? series.length > 1;
  const hasTitle = !!config.title;
  const xAxisLabel = buildXAxisLabel(categories.length, rawValues);

  const categoryAxis = {
    type: 'category' as const,
    data: categories,
    ...AXIS_X_STYLE,
    // En horizontal la catégorie passe sur yAxis : on garde le formatage des libellés mais
    // sans rotation (empilement vertical) et on inverse pour lire de haut en bas.
    axisLabel: horizontal ? { ...AXIS_X_STYLE.axisLabel, formatter: xAxisLabel.formatter } : xAxisLabel,
    ...(horizontal ? { inverse: true } : {}),
  };
  // `yAxis` (resolveDualAxis) est l'axe des VALEURS. En horizontal il devient l'axe X ;
  // un éventuel 2e axe (double échelle) passe de la droite au haut.
  const valueAxis =
    horizontal && Array.isArray(yAxis)
      ? yAxis.map((ax, i) => (i === 1 ? { ...ax, position: 'top' as const } : ax))
      : yAxis;

  const option = {
    title: hasTitle ? { text: config.title, ...TITLE_STYLE } : undefined,
    tooltip: buildTooltip(formats),
    legend: showLegend ? buildLegendStyle(undefined, legendPosition, hasTitle) : undefined,
    grid: horizontal
      ? {
          // containLabel : ECharts élargit la marge pour loger les libellés de catégorie
          // (noms de pays/campagnes, largeur très variable) — plus robuste qu'un left fixe.
          left: showLegend && legendPosition === 'left' ? '30%' : 8,
          right: showLegend && legendPosition === 'right' ? '30%' : 16,
          top: showLegend && legendPosition === 'top' ? (hasTitle ? 76 : 50) : hasTitle ? 40 : 16,
          bottom: showLegend && legendPosition === 'bottom' ? 36 : 8,
          containLabel: true,
        }
      : buildChartGrid({
          hasTitle,
          showYAxis,
          dualYAxis: dual,
          showLegend,
          legendPosition,
          rotatedXLabels: !!xAxisLabel.rotate,
        }),
    xAxis: horizontal ? valueAxis : categoryAxis,
    yAxis: horizontal ? categoryAxis : valueAxis,
    series,
  };

  return (
    <div className={SHELL}>
      <DrillBreadcrumb drillPath={drill.drillPath} catalog={catalog} onDrillTo={drill.drillTo} />
      {drill.isLoading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-[#8B8FA7]">Chargement…</div>
      ) : (
        <ReactECharts
          option={option}
          notMerge
          style={{ flex: 1, minHeight: 200 }}
          onEvents={
            drill.canDrillDown
              ? { click: (params: { name: string }) => void drill.handleDrillDown(params.name) }
              : {}
          }
        />
      )}
    </div>
  );
}
